import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Avatar, Box, Button, DialogContent, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';

//  mm:ss remaining until lockedUntil (server-authoritative). Empty once elapsed.
function formatRemaining(lockedUntil?: string | null): string {
  if (!lockedUntil) return '00:00';
  const ms = new Date(lockedUntil).getTime() - Date.now();
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

//  THIN-7681 lockout screen: too many wrong PINs. Viewing/editing still work; the
//  countdown ticks to the server's lockedUntil, and resetting the PIN in profile unlocks.
export default function SigningLockedDialog({
  open,
  lockedUntil,
  onClose,
}: {
  open: boolean;
  lockedUntil?: string | null;
  onClose: () => void;
}) {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  // recompute each tick
  const label = useMemo(() => formatRemaining(lockedUntil), [lockedUntil, tick]);

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark', mx: 'auto', mb: 1.5 }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography variant="h6" gutterBottom>
            Signing is locked
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Too many incorrect PIN attempts. You can keep working — viewing and editing documents
            still work. Signing unlocks in:
          </Typography>
          <Typography sx={{ fontSize: 40, fontWeight: 700, letterSpacing: 2, my: 2 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Forgot your PIN? Reset it from your profile to unlock immediately.
          </Typography>
          <Button variant="contained" fullWidth onClick={onClose}>
            Close
          </Button>
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
}
